import type { ComponentType } from "react";
import {
  IconActivity,
  IconCalendarEvent,
  IconClipboardText,
  IconCreditCard,
  IconFiles,
  IconForms,
  IconGift,
  IconNote,
  IconPackage,
  IconScissors,
  IconStar,
  IconUser,
  IconWriting,
} from "@tabler/icons-react";

/**
 * Feature availability that gates certain merge categories. Gift cards ship
 * behind an env flag and loyalty is enabled per salon, so those categories only
 * appear when the salon actually has the feature (the backend always folds the
 * balances onto the winner regardless — this only controls the preview).
 */
export interface MergeCategoryFlags {
  giftCardsEnabled?: boolean;
  loyaltyActive?: boolean;
}

/**
 * A category of historical data that gets combined onto the surviving profile
 * during a client merge. Some categories only apply to certain business
 * verticals (e.g. Formulations for hair/barber, Medical Charts for med spas) or
 * to salons with a feature enabled (Gift Cards, Loyalty Points), so the confirm
 * screen only renders the ones relevant to the salon.
 */
export interface MergeDataCategory {
  key: string;
  label: string;
  icon: ComponentType<{ size?: number; stroke?: number }>;
  /** Short badge shown next to vertical-specific categories. */
  badge?: string;
  /**
   * Verticals this category applies to. When omitted the category applies to
   * every vertical. Values match `salons.business_vertical`:
   * `hair` | `nail` | `barber` | `aesthetician` | `med_spa`.
   */
  verticals?: string[];
  /**
   * Optional feature gate. When present the category only shows if this returns
   * true for the salon's feature flags (used for Gift Cards and Loyalty Points).
   */
  requires?: (flags: MergeCategoryFlags) => boolean;
}

const HAIR_VERTICALS = ["hair", "barber"];
const MED_SPA_VERTICALS = ["med_spa"];

/**
 * Full catalog of combinable data categories, in display order. Filtered per
 * salon vertical by {@link getMergeDataCategories}.
 */
export const MERGE_DATA_CATEGORIES: MergeDataCategory[] = [
  { key: "details", label: "Client Details", icon: IconUser },
  { key: "payment_methods", label: "Payment Methods", icon: IconCreditCard },
  { key: "notes", label: "Notes", icon: IconNote },
  {
    key: "transactions",
    label: "Appointments / Quick Sales / Transactions",
    icon: IconCalendarEvent,
  },
  { key: "packages", label: "Packages", icon: IconPackage },
  {
    key: "gift_cards",
    label: "Gift Cards",
    icon: IconGift,
    requires: (flags) => !!flags.giftCardsEnabled,
  },
  {
    key: "loyalty",
    label: "Loyalty Points",
    icon: IconStar,
    requires: (flags) => !!flags.loyaltyActive,
  },
  {
    key: "formulations",
    label: "Formulations",
    icon: IconScissors,
    badge: "HAIR SALON ONLY",
    verticals: HAIR_VERTICALS,
  },
  {
    key: "medical_charts",
    label: "Medical Charts",
    icon: IconActivity,
    badge: "MED SPA ONLY",
    verticals: MED_SPA_VERTICALS,
  },
  {
    key: "soap_notes",
    label: "SOAP Notes",
    icon: IconClipboardText,
    badge: "MED SPA ONLY",
    verticals: MED_SPA_VERTICALS,
  },
  {
    key: "documents",
    label: "Documents",
    icon: IconFiles,
    badge: "MED SPA ONLY",
    verticals: MED_SPA_VERTICALS,
  },
  {
    key: "forms",
    label: "Forms",
    icon: IconForms,
    badge: "MED SPA ONLY",
    verticals: MED_SPA_VERTICALS,
  },
  {
    key: "waivers",
    label: "Waivers",
    icon: IconWriting,
    badge: "MED SPA ONLY",
    verticals: MED_SPA_VERTICALS,
  },
];

/**
 * Returns the combinable data categories relevant to a salon. Categories with no
 * `verticals` restriction always show; restricted ones only show when the
 * salon's vertical is included. Feature-gated categories (`requires`) show only
 * when the corresponding flag is set (Gift Cards, Loyalty Points).
 */
export function getMergeDataCategories(
  vertical?: string | null,
  flags: MergeCategoryFlags = {},
): MergeDataCategory[] {
  return MERGE_DATA_CATEGORIES.filter((cat) => {
    const verticalOk =
      !cat.verticals || (vertical != null && cat.verticals.includes(vertical));
    const requiresOk = !cat.requires || cat.requires(flags);
    return verticalOk && requiresOk;
  });
}
